/**
 * ポータルの Google ログインへの行き来（引換番号・2026-10-21〜）。
 *
 * 7日の証明書は URL に乗らない。ポータルは1回だけ・60秒だけ使える番号（portal_code）を付けて戻し、
 * API（trendscope-api の POST /auth/portal-code）が外から見えない通路でポータルに出して本人を受け取る。
 * 番号は、ここで作った合言葉（verifier）を持つこのブラウザでしか引き換えられない（PKCE・RFC 7636）。
 * だから番号が閲覧履歴などに残っても、それだけでは入れない。
 * 合言葉は state ごとに localStorage に置く（sessionStorage は、ポータルから戻ると別のタブの扱いになって読めないことがあるため）。
 * 1回読んだら消し、10分で捨てる。
 * 仕組みの正本: sl_management/knowledge/products/portal/specs/login-handoff-code.md
 */

const PORTAL_LOGIN = 'https://portal.supologe.com/login';
const FLOW_PREFIX = 'trendscope_portal_flow:';
const FLOW_TTL_MS = 10 * 60_000; // ポータルで Google のアカウントを選ぶ時間を見ても足りる長さ
const STATE = /^[A-Za-z0-9_-]{43}$/;
const CODE = /^[a-f0-9]{64}$/;

const base64url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** 32バイトの乱数（43文字）。合言葉と state に使う */
export const randomText = (): string => base64url(crypto.getRandomValues(new Uint8Array(32)));

/** 10分を過ぎた合言葉と、形の壊れた控えを捨てる */
const pruneFlows = (now: number): void => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(FLOW_PREFIX)) continue;
    try {
      const flow = JSON.parse(localStorage.getItem(key) ?? 'null') as { at?: unknown } | null;
      if (typeof flow?.at !== 'number' || now - flow.at > FLOW_TTL_MS) localStorage.removeItem(key);
    } catch {
      localStorage.removeItem(key);
    }
  }
};

/** ポータルのログインへ行く。戻ってきたら takePortalReturn で続きを受け取る */
export const startPortalLogin = async (): Promise<void> => {
  const now = Date.now();
  pruneFlows(now);
  const verifier = randomText();
  const state = randomText();
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  localStorage.setItem(FLOW_PREFIX + state, JSON.stringify({ verifier, at: now }));
  // 戻り先は、ポータルに登録したページ（app_url = https://trendscope-afm.pages.dev の `/`）。パスが違うとポータルは番号を出さない
  const back = new URL('/', window.location.origin);
  back.searchParams.set('portal_state', state);
  back.searchParams.set('portal_challenge', base64url(new Uint8Array(digest)));
  window.location.href = `${PORTAL_LOGIN}?return_to=${encodeURIComponent(back.href)}`;
};

export type PortalReturn =
  | { kind: 'code'; code: string; verifier: string }
  /** このブラウザで始めたログインの続きでない番号。code は形の正しいときだけ（使い切るため） */
  | { kind: 'invalid'; code: string | null }
  | null;

/**
 * 戻ってきた URL から番号を受け取り、このブラウザで始めたログインの続きか確かめる。
 * 番号が無ければ null。合言葉は、番号の有無にかかわらず1回で消す
 */
export const takePortalReturn = (search: string): PortalReturn => {
  const params = new URLSearchParams(search);
  const now = Date.now();
  const state = params.get('portal_state');
  let verifier: string | null = null;
  if (state !== null && STATE.test(state)) {
    const key = FLOW_PREFIX + state;
    try {
      const flow = JSON.parse(localStorage.getItem(key) ?? 'null') as { verifier?: unknown; at?: unknown } | null;
      if (typeof flow?.verifier === 'string' && typeof flow.at === 'number' && now - flow.at <= FLOW_TTL_MS) {
        verifier = flow.verifier;
      }
    } catch {
      // 壊れた控えは使わない
    }
    localStorage.removeItem(key);
  }
  pruneFlows(now);
  const code = params.get('portal_code');
  if (code === null) return null;
  if (!CODE.test(code)) return { kind: 'invalid', code: null };
  return verifier ? { kind: 'code', code, verifier } : { kind: 'invalid', code };
};

/** アドレス欄から portal_ で始まる値（番号・合図・旧方式の証明書）を全部消す。パス・ほかのクエリ・# はそのまま */
export const stripPortalParams = (): void => {
  const url = new URL(window.location.href);
  const keys = [...url.searchParams.keys()].filter((k) => k.startsWith('portal_'));
  if (!keys.length) return;
  for (const k of keys) url.searchParams.delete(k);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};
